import styled from "@emotion/styled";
import { Outlet, useLocation } from "react-router-dom";
import placeholderAvatar from "assets/images/default-avatar.svg";
import { Container } from "atoms";
import { useAuth } from "features/auth";
import { FollowInviteBanner } from "features/follows";
import { Head } from "features/misc";
import { type Artist } from "features/reviewable";
import { ArtistProfileHeader } from "./ArtistProfileHeader";
import { ArtistProfileTabs } from "./ArtistProfileTabs";

type ArtistProfileTemplateProps = {
  artist: Artist;
};

function ArtistProfileTemplate({ artist }: ArtistProfileTemplateProps) {
  const { user, roles } = useAuth();
  const { search, pathname } = useLocation();

  const isFollowInvite = search.endsWith("follow");
  const isPortfolio = pathname.endsWith("portfolio");

  return (
    <>
      <Head
        title={`${artist.name} | Artist | HUG`}
        description={`Learn more about artist ${artist.name}, discover and click through to shop their artwork, and connect with them by leaving reviews.`}
        socialTitle={`${artist.name} | HUG`}
        socialDescription={`Discover art from ${artist.name} and connect further by leaving a thoughtful and meaningful review.`}
        socialImage={
          artist.cardImage?.url ??
          artist.artistCollections?.[0]?.collectionImage?.url ??
          artist.heroImage?.url ??
          placeholderAvatar
        }
        socialImageAltText={`Artwork by ${artist.name}`}
        type="profile"
        username={artist.name}
      />

      {isFollowInvite && (
        <FollowInviteBanner
          artistId={artist.id}
          artistName={artist.name}
          user={user}
        />
      )}

      <ArtistProfileHeader
        artist={artist}
        user={user}
        showAdminFeatures={roles.showAdminFeatures}
        isPoster={roles.isPoster}
      />

      <ArtistProfileTabs artistId={artist.id} />

      <ArtistProfileOutletWrapper
        width="lg"
        // Hides overflow on Portfolio page and allows
        // `position: sticky` to work on other profile pages.
        style={isPortfolio ? { overflow: "hidden" } : {}}
      >
        <Outlet />
      </ArtistProfileOutletWrapper>
    </>
  );
}

const ArtistProfileOutletWrapper = styled(Container)({
  paddingBottom: "4rem",
  // Hidden overflow prevents position: sticky from working.
  // overflow: "hidden",
});

export { ArtistProfileTemplate };
